import type { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro';
import { Dayjs } from 'dayjs';
import { IconButton, SelectChangeEvent, Stack, Tooltip } from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import { REFETCH_INTERVAL_OPTIONS } from '../../constants';
import { usePagination } from '../../hooks/usePagination';
import { createColumn } from '../../utils';
import SelectRefreshInterval from '../atoms/SelectRefreshInterval';
import { ResetIcon } from '../icons';
import CollapsibleFilters from '../molecules/CollapsibleFilters';
import DateRangeSelect, { DayjsTimestamp } from '../molecules/DateRangeSelect';
import TableTemplate from '../templates/TableTemplate';
import useSort from '../../hooks/useSort';
import { createDateColumn, createNumberColumn } from '../../helpers/columns';
import { defaultColumnsApprovals } from '../../helpers/table';
import { Withdrawal } from '../../@types/api';
import { COLUMN_FIELDS } from '../../constants/table';
import ZeroXWithdrawalDetails from '../molecules/ZeroXWithdrawalDetails';
import WithdrawalStatusCell from '../molecules/WithdrawalStatusCell';
import { getWithdrawalActions } from '../../helpers/withdrawalActionPolicy';
import { formatPaymentMethod } from '../../helpers/paymentDisplay';
import usePermissions from '../../hooks/usePermissions';
import WithdrawalActionButtons from '../molecules/WithdrawalActionButtons';
import API_ROUTES from '../../constants/apiRoutes';
import ApprovalInfoCell from '../molecules/ApprovalInfoCell';
import { generateDateRangeTimestamps } from '../../helpers';

const { fromTimestamp, toTimestamp } = generateDateRangeTimestamps('last_7_days');

const ApprovalsPage = () => {
  const { payoutCapabilities } = usePermissions();
  const [date, setDate] = useState<{ fromTimestamp: Dayjs; toTimestamp: Dayjs }>({
    fromTimestamp,
    toTimestamp,
  });
  const [refetchInterval, setRefetchInterval] = useState<number | false>(0);

  const {
    data: approvalsData,
    updateQueryParams,
    isFetching,
    isLoading,
    changeQuery,
    refetch,
  } = usePagination<Withdrawal>(
    API_ROUTES.PAYMENT_REQUESTS,
    {
      page: 1,
      limit: 25,
      fromTimestamp: date.fromTimestamp.valueOf(),
      toTimestamp: date.toTimestamp.valueOf(),
    },
    {
      refetchInterval,
    },
    'wallet'
  );

  const { handleSort } = useSort(changeQuery);

  const handleDateChange = useCallback(
    (newDate: DayjsTimestamp) => {
      setDate(newDate);

      changeQuery({
        fromTimestamp: newDate.fromTimestamp.valueOf(),
        toTimestamp: newDate.toTimestamp.valueOf(),
      });
    },
    [changeQuery]
  );

  const handleRefetchIntervalChange = useCallback((e: SelectChangeEvent<number | false>) => {
    setRefetchInterval(e.target.value as number | false);
  }, []);

  const columns: GridColDef[] = useMemo(
    () => [
      createColumn(COLUMN_FIELDS.ID, 'Transaction ID'),
      createColumn(COLUMN_FIELDS.USER_ID, 'Player ID'),
      createColumn(COLUMN_FIELDS.FULL_NAME, 'Full Name', { sortable: false }),
      createColumn(COLUMN_FIELDS.USERNAME, 'User Name', { sortable: false }),
      createDateColumn(COLUMN_FIELDS.EXECUTION_TIME, 'Execution Time'),
      createNumberColumn(COLUMN_FIELDS.AMOUNT, 'Amount Requested'),
      createColumn('paymentProvider', 'Gateway', { sortable: false }),
      createColumn(COLUMN_FIELDS.PAYMENT_METHOD, 'Payment Method', {
        valueFormatter: (value, row: Withdrawal) => formatPaymentMethod(value, row),
      }),
      createColumn(COLUMN_FIELDS.STATUS, 'Status', {
        renderCell: (params: GridRenderCellParams<Withdrawal>) => <WithdrawalStatusCell withdrawal={params.row} />,
      }),
      createColumn(COLUMN_FIELDS.CRYPTO_WITHDRAWAL, 'Crypto withdrawal', {
        sortable: false,
        disableExport: true,
        minWidth: 180,
        renderCell: (params: GridRenderCellParams<Withdrawal>) => (
          <ZeroXWithdrawalDetails withdrawal={params.row.cryptoWithdrawal} />
        ),
      }),
      createColumn(COLUMN_FIELDS.IBAN, 'IBAN', {
        sortable: false,
        valueGetter: (_value, row) => row.bankTransfer?.IBAN,
      }),
      createColumn(COLUMN_FIELDS.SWIFT, 'Swift', {
        sortable: false,
        valueGetter: (_value, row) => row.bankTransfer?.swiftCode,
      }),
      createColumn(COLUMN_FIELDS.BENEFICIARY_NAME, 'Beneficiary Name', {
        sortable: false,
        valueGetter: (_value, row) => row.bankTransfer?.beneficiaryName,
      }),
      createColumn(COLUMN_FIELDS.BANK_NAME, 'Bank Name', {
        sortable: false,
        valueGetter: (_value, row) => row.bankTransfer?.bankName,
      }),
      createColumn(COLUMN_FIELDS.AUTH_RESULTS, 'Info', {
        sortable: false,
        renderCell: (params: GridRenderCellParams<Withdrawal>) => {
          return <ApprovalInfoCell row={params.row} />;
        },
      }),
      createColumn(COLUMN_FIELDS.ACTIONS, 'Actions', {
        minWidth: 240,
        sortable: false,
        renderCell: (params: GridRenderCellParams<Withdrawal>) => {
          const actions = getWithdrawalActions(params.row, payoutCapabilities);

          return <WithdrawalActionButtons transactionId={params.row.id} actions={actions} />;
        },
      }),
    ],
    [payoutCapabilities]
  );

  const quickFilterContent = useMemo(
    () => (
      <CollapsibleFilters>
        <DateRangeSelect date={date} onDateChange={handleDateChange} defaultDateRangeOption='last_7_days' />

        <Stack
          direction='row'
          spacing={1}
          sx={{
            alignItems: 'center',
          }}
        >
          <SelectRefreshInterval
            handleRefetchIntervalChange={handleRefetchIntervalChange}
            options={REFETCH_INTERVAL_OPTIONS}
            value={refetchInterval}
          />
          <Tooltip title='Refresh'>
            <IconButton onClick={() => refetch()}>
              <ResetIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </CollapsibleFilters>
    ),
    [date, handleDateChange, handleRefetchIntervalChange, refetchInterval, refetch]
  );

  return (
    <TableTemplate
      rows={approvalsData?.items || []}
      columns={columns}
      rowCount={approvalsData?.count || 0}
      loading={isLoading || isFetching}
      handlePaginationModelChange={updateQueryParams}
      handleSort={handleSort}
      changeQuery={changeQuery}
      customTableHeaderContent={quickFilterContent}
      defaultVisibleColumns={defaultColumnsApprovals}
    />
  );
};

export default ApprovalsPage;
